import apiService from '@/services/apiService'

const STORAGE_KEY = 'pos_operational_config_by_sucursal_v1'

// FH-5 (A11 del mapeo, SPEC_FIX_HEREDADOS_SUBS_v0.1.md). La configuración
// operativa vive en el API (`/api/inventory/operational-config/:sucursal_id`):
// es lo que el backend lee al cobrar. Antes esta pantalla guardaba SÓLO en
// localStorage y avisaba "guardada": el backend nunca se enteraba. Ahora
// localStorage es una caché de lectura (getConfig sigue sincrónico para sus
// nueve usos) y las claves que el backend no conoce son preferencias de la
// pantalla, que siguen locales.
const SERVER_KEYS = [
  'modo_operacion_sucursal',
  'deposito_mostrador_id',
  'deposito_default_entrada',
  'deposito_default_recepcion',
  'auto_transferir_a_mostrador',
  'consumo_venta_solo_mostrador',
  // P2 (SPEC_VENTAS_INNOMINADAS_FACTURACION_DIFERIDA_v0.1.md RN-VI2): cobro con
  // ticket de una venta sin documento. Lo lee el API al cobrar.
  'ventas_innominadas_ticket_habilitado',
  // Carga inicial de stock (SPEC_CARGA_INICIAL_STOCK_Y_PRECIOS_v0.1.md RH-1):
  // habilita la pantalla Compras → Carga inicial. El API también lo verifica.
  'carga_inicial_stock_habilitada'
]

const DEFAULT_CONFIG = {
  modo_operacion_sucursal: 'MULTIDEPOSITO_MANUAL',
  deposito_mostrador_id: null,
  deposito_default_entrada: null,
  deposito_default_recepcion: null,
  auto_transferir_a_mostrador: true,
  consumo_venta_solo_mostrador: true,
  ventas_innominadas_ticket_habilitado: false,
  carga_inicial_stock_habilitada: false,
  modo_cajero_unico: false,
  atajos_teclado_habilitados: true,
  mostrar_hints_atajos: true
}

function safeParse(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback
  } catch (_) {
    return fallback
  }
}

function getCurrentSucursalId() {
  const envVal = process.env.VUE_APP_SUCURSAL_ID
  if (envVal && !Number.isNaN(Number(envVal))) return Number(envVal)

  const ls = localStorage.getItem('sucursal_id') || localStorage.getItem('sucursalId')
  if (ls && !Number.isNaN(Number(ls))) return Number(ls)

  return 1
}

function getStorage() {
  return safeParse(localStorage.getItem(STORAGE_KEY), {})
}

function persistStorage(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

function normalizeBoolean(value, fallback) {
  return value === undefined || value === null ? fallback : Boolean(value)
}

function normalizeConfig(config = {}) {
  return {
    ...DEFAULT_CONFIG,
    ...config,
    deposito_mostrador_id: config?.deposito_mostrador_id != null ? Number(config.deposito_mostrador_id) : null,
    deposito_default_entrada: config?.deposito_default_entrada != null ? Number(config.deposito_default_entrada) : null,
    deposito_default_recepcion: config?.deposito_default_recepcion != null ? Number(config.deposito_default_recepcion) : null,
    auto_transferir_a_mostrador: config?.auto_transferir_a_mostrador !== false,
    consumo_venta_solo_mostrador: config?.consumo_venta_solo_mostrador !== false,
    ventas_innominadas_ticket_habilitado: config?.ventas_innominadas_ticket_habilitado === true,
    carga_inicial_stock_habilitada: config?.carga_inicial_stock_habilitada === true,
    modo_cajero_unico: normalizeBoolean(config?.modo_cajero_unico, false),
    atajos_teclado_habilitados: normalizeBoolean(config?.atajos_teclado_habilitados, true),
    mostrar_hints_atajos: normalizeBoolean(config?.mostrar_hints_atajos, true)
  }
}

function getConfig(sucursalId = getCurrentSucursalId()) {
  const storage = getStorage()
  return normalizeConfig(storage[String(sucursalId)] || {})
}

function cacheConfig(sucursalId, config) {
  const key = String(sucursalId || getCurrentSucursalId())
  const storage = getStorage()
  const nextConfig = normalizeConfig({ ...(storage[key] || {}), ...config })
  storage[key] = nextConfig
  persistStorage(storage)
  return nextConfig
}

const pickServerKeys = (config = {}) =>
  Object.fromEntries(SERVER_KEYS.filter((k) => k in config).map((k) => [k, config[k]]))

// Trae la configuración del API y refresca la caché. Si el API no responde,
// devuelve la caché sin romper la pantalla.
async function loadConfig(sucursalId = getCurrentSucursalId()) {
  try {
    const { data } = await apiService.get(`/api/inventory/operational-config/${sucursalId}`)
    return cacheConfig(sucursalId, pickServerKeys(data?.config || {}))
  } catch (error) {
    console.warn('No se pudo leer la configuración operativa del servidor', error)
    return getConfig(sucursalId)
  }
}

// Una vez por sesión (la barra de navegación la llama en cada pantalla): así
// un navegador que nunca abrió Mantenimiento operativo igual ve lo del
// servidor. Avisa con el mismo evento que el guardado, y las pantallas que
// leen la caché se refrescan.
let cargaDeSesion = null
function loadConfigOnce(sucursalId = getCurrentSucursalId()) {
  if (!cargaDeSesion) {
    cargaDeSesion = loadConfig(sucursalId).then((config) => {
      window.dispatchEvent(new CustomEvent('app:operational-config-changed', { detail: { sucursal_id: sucursalId } }))
      return config
    })
  }
  return cargaDeSesion
}

// Guarda en el API. Sólo si responde bien se actualiza la caché: si falla,
// lanza el error y la pantalla lo muestra (no dice "guardada").
async function saveConfig(sucursalId, payload) {
  const id = sucursalId || getCurrentSucursalId()
  const { data } = await apiService.put(`/api/inventory/operational-config/${id}`, pickServerKeys(payload))
  const servidor = pickServerKeys(data?.data?.config || {})
  const preferenciasLocales = Object.fromEntries(Object.entries(payload).filter(([k]) => !SERVER_KEYS.includes(k)))
  return cacheConfig(id, { ...preferenciasLocales, ...servidor })
}

function getOperationalSummary({ sucursalId = getCurrentSucursalId(), warehouses = [], config: explicitConfig = null } = {}) {
  const config = normalizeConfig(explicitConfig || getConfig(sucursalId))
  const activeEnabled = (warehouses || []).filter(dep => dep.activo && dep.habilitado)
  const counterWarehouse = activeEnabled.find(dep => dep.id === config.deposito_mostrador_id) || null
  const hasSingleOperationalWarehouse = activeEnabled.length === 1
  const singleOperationalWarehouse = hasSingleOperationalWarehouse ? activeEnabled[0] : null
  const canAutoUseCounter = Boolean(
    config.modo_operacion_sucursal === 'MONODEPOSITO_AUTO_MOSTRADOR' &&
    hasSingleOperationalWarehouse &&
    counterWarehouse &&
    counterWarehouse.id === singleOperationalWarehouse.id
  )

  return {
    sucursalId,
    config,
    activeEnabled,
    counterWarehouse,
    hasSingleOperationalWarehouse,
    singleOperationalWarehouse,
    canAutoUseCounter
  }
}

function resolveDestinationWarehouse({
  purpose = 'entrada',
  sucursalId = getCurrentSucursalId(),
  warehouses = [],
  config: explicitConfig = null
} = {}) {
  const summary = getOperationalSummary({ sucursalId, warehouses, config: explicitConfig })
  const { config, counterWarehouse, canAutoUseCounter } = summary

  if (canAutoUseCounter && counterWarehouse) {
    return {
      depositoId: counterWarehouse.id,
      autoResolved: true,
      requiresSelection: false,
      reason: 'mostrador_unico'
    }
  }

  const defaultWarehouseId = purpose === 'recepcion'
    ? config.deposito_default_recepcion
    : config.deposito_default_entrada
  const defaultWarehouse = summary.activeEnabled.find(dep => dep.id === defaultWarehouseId) || null

  if (config.modo_operacion_sucursal === 'MULTIDEPOSITO_DEPOSITO_DEFAULT' && defaultWarehouse) {
    return {
      depositoId: defaultWarehouse.id,
      autoResolved: true,
      requiresSelection: false,
      reason: purpose === 'recepcion' ? 'default_recepcion' : 'default_entrada'
    }
  }

  return {
    depositoId: null,
    autoResolved: false,
    requiresSelection: true,
    reason: 'seleccion_manual'
  }
}

function isAutoTransferMovement(movement) {
  const motivo = String(movement?.motivo || '').toLowerCase()
  return motivo.includes('auto-transferencia') || motivo.includes('autoabastecimiento')
}

export default {
  DEFAULT_CONFIG,
  getCurrentSucursalId,
  getConfig,
  loadConfig,
  loadConfigOnce,
  saveConfig,
  getOperationalSummary,
  resolveDestinationWarehouse,
  isAutoTransferMovement
}
